/**
 * Google access, asked for when a feature needs it.
 *
 * A first Google connection grants `basic` — files CorpBrain creates or the
 * person picks (Google's `drive.file`). `read` (see the person's own Drive)
 * and `edit` (change an existing file) are asked for the first time a feature
 * needs them, with Google's incremental consent in a popup, so the page — the
 * open picker, the running chat turn — stays exactly where it was.
 *
 * How the popup reports back (item 66): Google sends it to the public
 * callback, which finishes nothing — its landing page (`/google-access`)
 * hands Google's answer to the window that started THIS consent
 * (utils/oauthPopup.ts, a BroadcastChannel: Google's pages may cut
 * `window.opener`, so `popup.closed` is never an answer), and that window
 * finishes it as its own person (`oauth/finish`). A consent link opened by
 * someone else stores nothing.
 */

import { forgetDriveConnected } from '@/modules/conversations/services/driveService';
import { CloudStorageApi } from '@/modules/data-bundles/services/cloudStorageService';
import { runOAuthPopup } from '@/utils/oauthPopup';
import { parseUploadError } from '@/modules/conversations/services/fileValidationService';
import api from '@/services/api';

/** `basic` is a first connection (the conversation's connectors panel
 *  connects Google in the same popup); `read` / `edit` add to one. */
export type GoogleAccessLevel = 'basic' | 'read' | 'edit';
export type GoogleAccessOutcome = 'granted' | 'declined' | 'wrong_account' | 'error' | 'blocked' | 'cancelled';

/** Each level: true / false, or null while it cannot be known (Google's
 *  tokeninfo unavailable) — then the feature's own call decides. */
export interface GoogleAccessState {
  connected: boolean;
  basic: boolean | null;
  read: boolean | null;
  edit: boolean | null;
}

const NONE: GoogleAccessState = { connected: false, basic: false, read: false, edit: false };
const GIVE_UP_MS = 5 * 60 * 1000;

let statePromise: Promise<GoogleAccessState> | null = null;
const listeners = new Set<() => void>();

/** This person's Google access levels — one request, shared, until forgotten. */
export function getGoogleAccess(): Promise<GoogleAccessState> {
  if (!statePromise) {
    statePromise = api
      .get('/cloud-storage/capabilities')
      .then((res) => {
        const drive = res.data?.google_drive || {};
        const access = drive.access || {};
        const level = (v: unknown) => (v === null || v === undefined ? null : !!v);
        return { connected: !!drive.connected, basic: level(access.basic), read: level(access.read), edit: level(access.edit) };
      })
      .catch(() => {
        statePromise = null;  // a failed read is not an answer — ask again next time
        return NONE;
      });
  }
  return statePromise;
}

/** Drop the cached levels (after a consent) and tell every subscriber. */
export function forgetGoogleAccess(): void {
  statePromise = null;
  forgetDriveConnected();
  listeners.forEach((fn) => fn());
}

export function subscribeGoogleAccess(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Is this the 403 GOOGLE_ACCESS_NEEDED a Drive call answers when the person
 *  has not granted the level it needs? (The backend's FileError contract.) */
export function isGoogleAccessNeeded(err: unknown): boolean {
  return parseUploadError(err)?.code === 'GOOGLE_ACCESS_NEEDED';
}

/** "Not now" — a tool waiting in a chat turn reads it and carries on without. */
export async function declineGoogleAccess(): Promise<void> {
  try {
    await api.post('/cloud-storage/google_drive/access/decline');
  } catch {
    // The wait ends on its own timeout; a lost "Not now" costs nothing more.
  }
}

export interface AccessRequest {
  outcome: Promise<GoogleAccessOutcome>;
  cancel: () => void;
}

/**
 * Ask Google for `level` in a popup. MUST be called from the click handler
 * itself: the window is opened synchronously (a popup opened after an await
 * is blocked) and pointed at Google once the consent URL arrives.
 */
export function requestGoogleAccess(level: GoogleAccessLevel): AccessRequest {
  const popup = window.open('', 'corpbrain-google-access', 'popup,width=520,height=700');
  if (!popup) return { outcome: Promise.resolve('blocked'), cancel: () => {} };
  const run = runOAuthPopup(popup, {
    start: () => CloudStorageApi.startAuthorization('google_drive', { access: level, mode: 'popup' }),
    finish: (callback) => CloudStorageApi.finishAuthorization('google_drive', callback),
    limitMs: GIVE_UP_MS,
  });
  const outcome = run.outcome
    .then((finished): GoogleAccessOutcome =>
      (['granted', 'declined', 'wrong_account', 'cancelled'] as const).find((o) => o === finished) ?? 'error')
    .catch((): GoogleAccessOutcome => 'error')
    .then((result) => {
      // Only a grant changes anything on the server.
      if (result === 'granted') forgetGoogleAccess();
      return result;
    });
  return { outcome, cancel: run.cancel };
}
